import { Platform } from 'react-native';

// On web, the browser is on the same machine as the backend → use localhost.
// On native (iOS/Android device or emulator), use the machine's LAN IP.
const DEV_NATIVE_IP = '10.50.78.78';

const STAGING_API_URL = 'https://lnb-backend-staging-geusdhf7kq-el.a.run.app';

const DEFAULT_API_URLS = {
  development: Platform.OS === 'web'
    ? 'http://localhost:8082'
    : `http://${DEV_NATIVE_IP}:8082`,
  staging: STAGING_API_URL,
  // Production Cloud Run is not live; that URL returns HTML 404.
  production: STAGING_API_URL,
} as const;

type EnvironmentKey = keyof typeof DEFAULT_API_URLS;

const sanitizeBaseUrl = (url: string) => url.replace(/\/+$/, '');

// Avoid optional chaining on process.env.* — webpack/babel env inlining turns
// missing vars into `undefined`, which can compile `env?.trim()` into `(void 0)()`.
const rawApiUrl = String(process.env.EXPO_PUBLIC_API_URL || '').trim() || undefined;
const rawFirebaseEnv = String(process.env.EXPO_PUBLIC_FIREBASE_ENV || '').trim().toLowerCase();
const envName = (rawFirebaseEnv as EnvironmentKey) || (__DEV__ ? 'development' : 'staging');
const fallbackApiUrl = DEFAULT_API_URLS[envName] || DEFAULT_API_URLS.development;

// Only log environment variables in development to prevent exposure in production
if (__DEV__) {
  console.log('🔧 ENV - process.env.EXPO_PUBLIC_API_URL:', process.env.EXPO_PUBLIC_API_URL);
  console.log('🔧 ENV - rawApiUrl:', rawApiUrl);
  console.log('🔧 ENV - __DEV__:', __DEV__);
  console.log('🔧 ENV - EXPO_PUBLIC_FIREBASE_ENV:', process.env.EXPO_PUBLIC_FIREBASE_ENV);
}

/**
 * Shared API base URL for all network calls. Updates without a new build by
 * reading the EXPO_PUBLIC_API_URL env var (falls back to a sane default for dev).
 */
export const apiBaseUrl = sanitizeBaseUrl(rawApiUrl || fallbackApiUrl);

console.log('🔧 ENV - Final apiBaseUrl:', apiBaseUrl);

export const buildApiUrl = (path = '') => {
  const normalizedPath = path.replace(/^\/+/, '');
  if (!normalizedPath) {
    return apiBaseUrl;
  }
  return `${apiBaseUrl}/${normalizedPath}`;
};

export async function readApiJson<T = any>(response: Response): Promise<T> {
  const contentType = response.headers.get('content-type') || '';
  const text = await response.text();

  if (!text) {
    throw new Error(`Empty response from server (${response.status}). Please try again.`);
  }

  const looksLikeHtml =
    text.trimStart().startsWith('<') || contentType.includes('text/html');

  if (looksLikeHtml) {
    throw new Error(
      'Unable to reach the LNB server. The app received an HTML error page instead of data. Please try again later.'
    );
  }

  try {
    return JSON.parse(text) as T;
  } catch {
    throw new Error('Unable to reach the LNB server. Please try again later.');
  }
}

// Video duration limits
export const VIDEO_MIN_DURATION = parseInt(process.env.EXPO_PUBLIC_VIDEO_MIN_DURATION || '10', 10);
export const VIDEO_MAX_DURATION = parseInt(process.env.EXPO_PUBLIC_VIDEO_MAX_DURATION || '30', 10);

if (__DEV__ && !rawApiUrl) {
  console.warn(`[env] EXPO_PUBLIC_API_URL is not set. Falling back to ${fallbackApiUrl}.`);
}
